import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, Modal, PrimaryBtn, inputCls, Field, errMsg } from './finance/shared';
import Icon from '../../components/dashboard/DashboardIcons';
import { buildWaMeLink, normalizeWhatsAppNumber } from '../../lib/whatsapp';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (v) => Math.round(Number(v || 0)).toLocaleString('en-US');
const STATUS = { trial: 'تجريبية', active: 'نشطة', expired: 'منتهية', cancelled: 'ملغاة', pending: 'قيد المراجعة' };

const FAQS = [
  { q: 'هل أفقد بياناتي إذا انتهى الاشتراك؟', a: 'لا. تبقى جميع بياناتك محفوظة بالكامل — اللاعبون والاشتراكات والتقارير والمالية — ويعود الوصول فور التجديد دون فقدان أي شيء.' },
  { q: 'هل يمكنني الترقية في منتصف الشهر؟', a: 'نعم. عند الترقية نحتسب لك قيمة المدة المتبقية من باقتك الحالية وتُخصم من قيمة الباقة الجديدة.' },
  { q: 'ما الفرق بين الاشتراك الشهري والسنوي؟', a: 'الاشتراك السنوي يمنحك نفس المزايا بسعر أقل إجمالاً، ويعفيك من التجديد الشهري. يمكنك التحويل بينهما في أي وقت.' },
  { q: 'كيف أدفع؟', a: 'بعد إرسال طلب الترقية يتواصل معك فريق تسيار خلال ساعات عمل قصيرة لتأكيد التفاصيل وإرسال وسيلة الدفع المناسبة (تحويل بنكي أو بطاقة).' },
];

const Check = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-4 w-4 shrink-0 text-teal-500">
    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
  </svg>
);

const PlanCard = ({ plan, billing, isCurrent, onPick, index }) => {
  const annual = billing === 'annual';
  const price = annual ? (plan.annual_price_effective ?? plan.price * 12) : plan.price;
  const hero = plan.is_recommended;
  return (
    <div
      data-testid={`plan-card-${plan.plan_id}`}
      style={{ animation: 'ccRise .5s cubic-bezier(.2,.7,.3,1) both', animationDelay: `${index * 70}ms` }}
      className={`relative flex flex-col overflow-hidden rounded-2xl border p-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${
        hero ? 'border-teal-400/30 bg-[#0f1726] text-white shadow-2xl shadow-slate-900/30 lg:-mt-3 lg:pb-7' : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      {hero && <span className="pointer-events-none absolute -left-16 -top-20 h-48 w-48 rounded-full bg-teal-500/20 blur-3xl" />}
      <div className="relative flex items-start justify-between gap-2">
        <div>
          <h3 className={`text-base font-extrabold ${hero ? 'text-white' : 'text-slate-900'}`}>{plan.display_name}</h3>
          <p className={`mt-0.5 text-[11px] ${hero ? 'text-white/50' : 'text-slate-400'}`}>{plan.tagline}</p>
        </div>
        {plan.is_recommended && <span className="shrink-0 rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold text-white">الأكثر اختياراً</span>}
        {isCurrent && !plan.is_recommended && <span className="shrink-0 rounded-full bg-teal-50 px-2.5 py-1 text-[10px] font-bold text-teal-700">باقتك الحالية</span>}
      </div>

      <div className="relative mt-4 flex items-end gap-1.5">
        <span className={`text-4xl font-extrabold leading-none tabular-nums ${hero ? 'text-teal-300' : 'text-slate-900'}`}>{price === 0 ? 'مجاناً' : money(price)}</span>
        {price > 0 && <span className={`pb-1 text-xs font-semibold ${hero ? 'text-white/50' : 'text-slate-400'}`}>ريال / {annual ? 'سنة' : 'شهر'}</span>}
      </div>
      {annual && plan.price > 0 && plan.annual_savings_percent > 0 && (
        <span className="relative mt-1.5 w-fit rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">توفير {plan.annual_savings_percent}% عن الشهري</span>
      )}
      {!annual && plan.price === 0 && <span className={`relative mt-1 text-[11px] ${hero ? 'text-white/50' : 'text-slate-400'}`}>{plan.billing_period}</span>}

      <ul className="relative mt-4 flex-1 space-y-2">
        {(plan.features || []).map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-xs ${hero ? 'text-white/80' : 'text-slate-600'}`}>
            <Check /> <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button" onClick={() => onPick(plan)} disabled={isCurrent}
        data-testid={`plan-cta-${plan.plan_id}`}
        className={`relative mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-[.98] disabled:cursor-default ${
          isCurrent ? (hero ? 'bg-white/10 text-white/50' : 'bg-slate-100 text-slate-400')
            : hero ? 'bg-teal-500 text-white hover:bg-teal-400' : 'bg-[#16233b] text-white hover:bg-[#1e3050]'
        }`}
      >
        {isCurrent ? 'باقتك الحالية' : (plan.cta_text || 'اطلب الترقية')}
      </button>
    </div>
  );
};

export default function SubscriptionPlansPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [faqOpen, setFaqOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/saas/my-club/subscription-overview`, auth());
      setData(res.data);
    } catch (e) { setErr(errMsg(e, 'تعذّر جلب بيانات الاشتراك')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const maxAnnualDiscount = Math.max(
    0,
    ...(data?.plans || []).filter((p) => p.price > 0).map((p) => Number(p.annual_savings_percent ?? p.annual_discount_percent) || 0)
  );

  const submitRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/saas/my-club/upgrade-request`, { plan_id: picked.plan_id, billing, note: note.trim() || null }, auth());
      setSent(picked.display_name);
      setPicked(null); setNote('');
    } catch (e2) { setErr(errMsg(e2, 'تعذّر إرسال الطلب')); }
    setBusy(false);
  };

  const cur = data?.current;
  const contact = data?.contact || {};
  const wa = normalizeWhatsAppNumber(contact.phone);
  const waLink = wa ? buildWaMeLink(contact.phone, `السلام عليكم، أنا ${cur?.club_name || ''} وأحتاج مساعدة بخصوص باقة الاشتراك.`) : null;
  const daysLeft = cur?.days_left;
  const membershipText = (() => {
    const d = cur?.member_days;
    if (d === null || d === undefined) return '';
    if (d < 30) return `${d} يوم معنا`;
    const years = Math.floor(d / 365);
    const months = Math.floor((d % 365) / 30);
    const parts = [];
    if (years === 1) parts.push('سنة'); else if (years === 2) parts.push('سنتان'); else if (years > 2) parts.push(`${years} سنوات`);
    if (months === 1) parts.push('شهر'); else if (months === 2) parts.push('شهران'); else if (months > 2) parts.push(`${months} أشهر`);
    return `مضى ${parts.join(' و')} معنا`;
  })();
  const isHealthy = cur?.status === 'active' && (daysLeft === null || daysLeft === undefined || daysLeft > 7);

  return (
    <div className="space-y-6 pb-10" data-testid="subscription-plans-page">
      {/* الاشتراك الحالي */}
      <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#16233b] via-[#131f35] to-[#0d1626] p-6 text-white shadow-xl" data-testid="current-subscription-hero">
        <span className="pointer-events-none absolute -left-16 -top-24 h-56 w-56 rounded-full bg-teal-500/15 blur-3xl" />
        <span className="pointer-events-none absolute -bottom-24 -right-10 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-teal-300">
              <Icon name="star" className="h-3 w-3 text-orange-400" /> اشتراك منشأتك
            </span>
            <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl" data-testid="current-plan-title">
              {data?.plans?.find((p) => p.plan_id === cur?.plan_id)?.display_name || 'لا توجد باقة'}
            </h1>
            <p className="mt-1 text-xs text-slate-300">
              الحالة: <b className={isHealthy ? 'text-teal-300' : 'text-orange-300'}>{STATUS[cur?.status] || '—'}</b>
              {cur?.end_date && ` · تنتهي في ${new Date(cur.end_date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { dateStyle: 'medium' })}`}
            </p>
            {cur?.created_at && (
              <p className="mt-2 inline-flex flex-wrap items-center gap-1.5 rounded-lg bg-white/[0.04] px-2.5 py-1.5 text-[11px] text-white/70" data-testid="membership-since">
                <Icon name="calendar" className="h-3.5 w-3.5 text-teal-300" />
                عضو في {contact.platform_name || 'تسيار'} منذ {new Date(cur.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { dateStyle: 'long' })}
                <b className="text-white/90">· {membershipText}</b>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-stretch gap-3">
          {cur?.member_days !== null && cur?.member_days !== undefined && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center backdrop-blur-xl" data-testid="member-days-card">
              <div className="text-3xl font-extrabold tabular-nums text-white">{cur.member_days.toLocaleString('en-US')}</div>
              <div className="text-[11px] text-white/60">يوم معنا</div>
            </div>
          )}
          {daysLeft !== null && daysLeft !== undefined && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center backdrop-blur-xl" data-testid="days-left-card">
              <div className={`text-3xl font-extrabold tabular-nums ${daysLeft < 0 ? 'text-red-400' : daysLeft <= 7 ? 'text-orange-400' : 'text-teal-300'}`}>{Math.abs(daysLeft)}</div>
              <div className="text-[11px] text-white/60">{daysLeft < 0 ? 'يوم منذ الانتهاء' : 'يوم متبقٍ'}</div>
            </div>
          )}
          </div>
        </div>
      </section>

      {err && <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="plans-error">{err}</div>}
      {sent && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="upgrade-request-success">
          <span className="text-sm font-semibold text-emerald-800">تم إرسال طلبك لباقة «{sent}» — سيتواصل معك فريق تسيار قريباً.</span>
          <button onClick={() => setSent(null)} className="text-xs font-bold text-emerald-700 hover:underline" data-testid="upgrade-success-dismiss">حسناً</button>
        </div>
      )}

      {/* الباقات */}
      <section className="space-y-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <div>
            <h2 className="text-lg font-bold text-slate-900">باقات الاشتراك</h2>
            <p className="mt-0.5 text-xs text-slate-400">اختر ما يناسب حجم منشأتك — ويمكنك التغيير في أي وقت</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="billing-toggle">
              {[['monthly', 'شهري'], ['annual', 'سنوي']].map(([k, label]) => (
                <button key={k} onClick={() => setBilling(k)} data-testid={`billing-${k}`}
                  className={`rounded-lg px-5 py-1.5 text-xs font-bold transition-colors ${billing === k ? 'bg-[#16233b] text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}>
                  {label}
                </button>
              ))}
            </div>
            {maxAnnualDiscount > 0 && (
              <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700" data-testid="annual-saving-hint">
                وفّر حتى {maxAnnualDiscount}% سنوياً
              </span>
            )}
          </div>
        </div>

        {!data ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-slate-200/70" />)}</div>
        ) : (
          <div
            className={`mx-auto grid w-full grid-cols-1 items-stretch gap-4 ${
              data.plans.length === 1 ? 'max-w-sm'
                : data.plans.length === 2 ? 'max-w-2xl sm:grid-cols-2'
                : data.plans.length === 3 ? 'max-w-4xl sm:grid-cols-2 lg:grid-cols-3'
                : 'sm:grid-cols-2 lg:grid-cols-4'
            }`}
            data-testid="plans-grid"
          >
            {data.plans.map((p, i) => (
              <PlanCard key={p.plan_id} plan={p} index={i} billing={billing} isCurrent={p.plan_id === cur?.plan_id} onPick={setPicked} />
            ))}
          </div>
        )}
      </section>

      {/* تواصل معنا */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="contact-us-section">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <h2 className="text-lg font-bold text-slate-900">تحتاج مساعدة في الاختيار؟</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            تواصل مع فريق {contact.platform_name || 'تسيار'} مباشرة وسنساعدك في اختيار الباقة الأنسب لعدد لاعبيك وفرقك، أو نرتب لك عرضاً مخصصاً لمنشأتك.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
                className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600">
                <Icon name="whatsapp" className="h-4 w-4" /> تواصل عبر واتساب
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} data-testid="contact-email-btn"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50">
                <Icon name="inbox" className="h-4 w-4" /> {contact.email}
              </a>
            )}
          </div>
          <div className="mt-3 flex gap-3 text-xs text-slate-400">
            {contact.twitter_url && <a href={contact.twitter_url} target="_blank" rel="noreferrer" className="hover:text-slate-700" data-testid="contact-twitter">تويتر / X</a>}
            {contact.instagram_url && <a href={contact.instagram_url} target="_blank" rel="noreferrer" className="hover:text-slate-700" data-testid="contact-instagram">إنستغرام</a>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5" data-testid="plans-faq">
          <h3 className="text-sm font-bold text-slate-800">أسئلة شائعة</h3>
          <div className="mt-3 space-y-1.5">
            {FAQS.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-xl bg-white">
                <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} data-testid={`faq-toggle-${i}`}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-right text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50">
                  {f.q}
                  <span className="shrink-0 text-slate-300">{faqOpen === i ? '−' : '+'}</span>
                </button>
                {faqOpen === i && <p className="border-t border-slate-100 px-3 py-2.5 text-[11px] leading-relaxed text-slate-500">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Modal open={Boolean(picked)} title={`طلب باقة ${picked?.display_name || ''}`} onClose={() => setPicked(null)}>
        <form onSubmit={submitRequest} className="space-y-3">
          <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
            <div className="flex justify-between"><span>الباقة المطلوبة</span><b className="text-slate-800">{picked?.display_name}</b></div>
            <div className="mt-1 flex justify-between"><span>نوع الاشتراك</span><b className="text-slate-800">{billing === 'annual' ? 'سنوي' : 'شهري'}</b></div>
            <div className="mt-1 flex justify-between">
              <span>القيمة</span>
              <b className="text-slate-800 tabular-nums">
                {picked?.price === 0 ? 'مجاناً' : `${money(billing === 'annual' ? (picked?.annual_price_effective ?? picked?.price * 12) : picked?.price)} ريال`}
              </b>
            </div>
          </div>
          <Field label="ملاحظة لفريق تسيار (اختياري)">
            <textarea rows={3} className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="مثال: نحتاج تفعيلها قبل بداية الموسم، وعدد لاعبينا 240" data-testid="upgrade-note-input" />
          </Field>
          <p className="text-[11px] text-slate-400">سيصل طلبك لفريق تسيار مع بيانات منشأتك ورقم التواصل، ولا يتم أي خصم تلقائي.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setPicked(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={busy} data-testid="upgrade-submit-btn">{busy ? 'جارٍ الإرسال...' : 'إرسال الطلب'}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
